// @flow

/**
 * Prompt Chat Reducer
 * Generic reducer for managing chat state transitions
 */

import type { ChatState, PromptChatAction } from '../types';

/**
 * Finalizes an event by setting end time, calculating duration, and clearing animation
 * @param {Object} event - Event to finalize
 * @param {Object} opts - Options including error flag
 * @returns {Object} Finalized event
 */
function finalizeEvent(event, opts = {}) {
  const now = Date.now();
  const finalized = { ...event };
  finalized.withDots = false;
  finalized.endAt = now;
  if (typeof event.startAt === 'number') {
    finalized.durationMs = Math.max(0, now - event.startAt);
  }
  if (opts.error !== undefined) {
    finalized.error = opts.error;
  }
  return finalized;
}

/**
 * Chat reducer handling all state transitions
 * Rules:
 * - Only clone the message being updated
 * - When adding a new event: if the new event has an eventId (lifecycle-managed),
 *   only finalize non-lifecycle active events; if no eventId (legacy), finalize all
 * - On first token, finalize all animating events then append text
 * - FINALIZE_EVENT_BY_ID: finalize a specific event by its eventId
 * - On generic error or cancel: finalize all active events with error:true
 * - On workflow-finished: finalize all active events with error:false
 * - On normal stream close: finalize all active events (no error field)
 */
export function promptChatReducer(state: ChatState, action: PromptChatAction): ChatState {
  switch (action.type) {
    case 'START_STREAM':
      return {
        ...state,
        activeStreamMessageId: action.agentMessageId,
        isStreaming: true,
        promptLocked: true,
      };

    case 'END_STREAM':
      return {
        ...state,
        activeStreamMessageId: null,
        isStreaming: false,
        promptLocked: false,
      };

    case 'ADD_USER_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.message],
      };

    case 'ADD_AGENT_PLACEHOLDER':
      return {
        ...state,
        messages: [...state.messages, action.message],
      };

    case 'APPEND_AGENT_TEXT': {
      const { agentMessageId, textChunk } = action;
      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id !== agentMessageId) return msg;
          
          const events = Array.isArray(msg.events) ? [...msg.events] : [];
          const isFirstToken = !msg.text || msg.text.length === 0;
          
          // First token: finalize all animating events (agent text means all tools are done)
          if (isFirstToken && events.length > 0) {
            for (let i = 0; i < events.length; i++) {
              if (events[i].withDots) {
                events[i] = finalizeEvent(events[i]);
              }
            }
          }
          
          return {
            ...msg,
            text: (msg.text || '') + textChunk,
            events,
          };
        }),
      };
    }

    case 'APPEND_EVENT': {
      const { agentMessageId, event } = action;
      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id !== agentMessageId) return msg;

          const events = Array.isArray(msg.events) ? [...msg.events] : [];

          // Selective finalization based on whether new event is lifecycle-managed
          for (let i = 0; i < events.length; i++) {
            if (!events[i].withDots) continue;
            if (event.eventId) {
              // New lifecycle event (tool start): only finalize non-lifecycle events
              if (!events[i].eventId) {
                events[i] = finalizeEvent(events[i]);
              }
            } else {
              // Legacy event (no eventId): finalize all active events
              events[i] = finalizeEvent(events[i]);
            }
          }

          events.push(event);
          return { ...msg, events };
        }),
      };
    }

    case 'FINALIZE_LAST_EVENT': {
      const { agentMessageId, opts } = action;
      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id !== agentMessageId) return msg;

          const events = Array.isArray(msg.events) ? [...msg.events] : [];
          // Finalize all active events (parallel tools may still be running at stream end)
          for (let i = 0; i < events.length; i++) {
            if (events[i].withDots) {
              events[i] = finalizeEvent(events[i], opts || {});
            }
          }

          return { ...msg, events };
        }),
      };
    }

    case 'FINALIZE_EVENT_BY_ID': {
      const { agentMessageId, eventId, opts } = action;
      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id !== agentMessageId) return msg;

          const events = Array.isArray(msg.events) ? [...msg.events] : [];
          const idx = events.findIndex(e => e.eventId === eventId && e.withDots);
          if (idx !== -1) {
            events[idx] = finalizeEvent(events[idx], opts || {});
          }

          return { ...msg, events };
        }),
      };
    }

    case 'SET_TRACE_ID': {
      const { agentMessageId, traceId, sessionId } = action;
      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id !== agentMessageId) return msg;
          return { ...msg, traceId, sessionId };
        }),
      };
    }

    case 'SET_FEEDBACK': {
      const { messageId, feedback } = action;
      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id !== messageId) return msg;
          return { ...msg, feedback };
        }),
      };
    }

    case 'RESET_CHAT': {
      const { initialAgentText, newSessionId } = action;
      return {
        ...state,
        messages: [
          {
            id: `a-${Date.now()}`,
            role: 'agent',
            text: initialAgentText,
            isIntro: true,
          },
        ],
        activeStreamMessageId: null,
        isStreaming: false,
        sessionId: newSessionId,
        error: null,
        promptLocked: false,
      };
    }

    case 'SET_ERROR':
      return {
        ...state,
        error: action.error,
      };

    case 'LOCK_PROMPT':
      return {
        ...state,
        promptLocked: action.locked,
      };

    case 'APPEND_DATA_DISPLAY': {
      const { agentMessageId, displayId, definition } = action;
      return {
        ...state,
        messages: state.messages.map((msg) => {
          if (msg.id !== agentMessageId) return msg;
          return {
            ...msg,
            dataDisplays: {
              ...(msg.dataDisplays || {}),
              [displayId]: definition,
            },
          };
        }),
      };
    }

    case 'HYDRATE_MESSAGES': {
      const { messages, sessionId } = action;
      return {
        ...state,
        messages: messages || [],
        sessionId: sessionId || state.sessionId,
        activeStreamMessageId: null,
        isStreaming: false,
        error: null,
        promptLocked: false,
      };
    }

    default:
      return state;
  }
}

/**
 * Create initial chat state
 * @param {string} sessionId - Initial session ID
 * @param {string} initialAgentText - Intro message text (if not suppressed)
 * @param {boolean} suppressIntro - Whether to suppress intro message
 * @returns {Object} Initial state
 */
export function createInitialState(
  sessionId: string,
  initialAgentText: string,
  suppressIntro: boolean = false
): ChatState {
  // Suppress intro if explicitly requested or if initialAgentText is empty
  const shouldSuppressIntro = suppressIntro || !initialAgentText || initialAgentText.trim() === '';
  
  return {
    messages: shouldSuppressIntro ? [] : [
      {
        id: `a-${Date.now()}`,
        role: 'agent',
        text: initialAgentText,
        isIntro: true,
      },
    ],
    activeStreamMessageId: null,
    isStreaming: false,
    sessionId: sessionId || '',
    error: null,
    promptLocked: false,
  };
}
